'use client';

import Button from '@/components/ui/Button';
import ErrorText from '@/components/ui/ErrorText';
import Input from '@/components/ui/Input';
import Link from '@/components/ui/Link';
import { PathEnum } from '@/i18n/locale-config';
import { createClient } from '@/utils/supabase/client';
import { cn } from '@/utils/tailwind-merge';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const resetPasswordSchema = z.object({
    email: z.string().email('Invalid email address').nonempty('Email is required'),
});

type FormData = z.infer<typeof resetPasswordSchema>;

const ForgotPasswordPage = () => {
    const t = useTranslations();
    const locale = useLocale();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const supabase = createClient();

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(resetPasswordSchema),
    });

    const onSubmit = async (formData: FormData) => {
        setSubmitting(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            console.log(`${window.location.origin}/${locale}/reset-password`);
            const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
                redirectTo: `${window.location.origin}/${locale}/reset-password`,
            });
            if (error) {
                setErrorMessage(t('PasswordReset.errorMessage'));
                return;
            }
            setSuccessMessage(t('PasswordReset.successMessage'));
            reset();
        } catch (error) {
            setErrorMessage(t('PasswordReset.errorMessage'));
        }

        setSubmitting(false);
    };

    const getFieldError = (fieldName: keyof FormData) =>
        errors[fieldName]?.message && errors[fieldName]?.message !== 'Required'
            ? t(`FieldErrors.${errors[fieldName]?.message}`)
            : '';

    return (
        <form
            className={cn('flex flex-col gap-7 justify-center items-center relative text-white')}
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="text-center">
                <h4 className="mb-5 text-h4 lg:text-h1 uppercase text-white">
                    {t('PasswordReset.title')}
                </h4>
                <p className="font-helvetica-neue font-light text-xl">
                    {t('PasswordReset.description')}
                </p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-3 max-w-[460px] w-full">
                <Input
                    {...register('email')}
                    className="newsletter-input"
                    type="email"
                    name="email"
                    id="email"
                    placeholder={t('Form.email')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('email')}
                    required
                />
                {errors && (
                    <div className="font-helvetica-neue font-light flex flex-col text-center">
                        <ErrorText error={getFieldError('email')} />
                    </div>
                )}
                {errorMessage && (
                    <div className="font-helvetica-neue font-light flex flex-col text-center">
                        <ErrorText error={errorMessage} />
                    </div>
                )}
                {successMessage && (
                    <div className="font-helvetica-neue font-light flex flex-col text-center text-green-500">
                        {successMessage}
                    </div>
                )}
                <Button
                    className="w-full"
                    textClasses="text-white"
                    type="submit"
                    variant="secondary"
                    title={t('Form.next')}
                    submitting={submitting}
                    disabled={submitting}
                />
                <p className="mt-7 text-xl font-helvetica-neue font-light leading-6 text-center">
                    <Link
                        href={PathEnum.SignIn}
                        className="text-primary"
                        title={t('PasswordReset.backToSignIn')}
                    />
                </p>
            </div>
        </form>
    );
};

export default ForgotPasswordPage;
